/**
 * vietnameseNumberToWords.ts
 * Convert a number to its Vietnamese words representation, e.g.:
 *   1_250_000  → "một triệu hai trăm năm mươi nghìn"
 *   123        → "một trăm hai mươi ba"
 *   -45        → "âm bốn mươi lăm"
 *
 * Used by the ⟦TEXT:fieldName⟧ marker so invoices / accounting documents can
 * spell out an amount in words (e.g. "Số tiền bằng chữ: một triệu ... đồng").
 *
 * Implements the standard Vietnamese reading rules:
 *   - groups of 3 digits (nghìn, triệu, tỷ, ...)
 *   - "mươi" / "lăm" / "linh" / "tư" context rules
 *   - "một" → "mốt" after a ten when followed by nothing smaller
 *   - "năm" → "lăm" after a ten
 *   - "bốn" → "tư" after a ten (unit only)
 */

const UNITS = [
  "",
  "nghìn",
  "triệu",
  "tỷ",
  "nghìn tỷ",
  "triệu tỷ",
  "tỷ tỷ",
];

const DIGITS = [
  "",
  "một",
  "hai",
  "ba",
  "bốn",
  "năm",
  "sáu",
  "bảy",
  "tám",
  "chín",
];

/** Read a 0–999 block. Returns the Vietnamese phrase (may be empty for 0). */
function readHundreds(n: number): string {
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  const ten = Math.floor(rest / 10);
  const unit = rest % 10;

  const parts: string[] = [];
  if (hundred > 0) {
    parts.push(DIGITS[hundred]);
    parts.push("trăm");
    if (rest === 0) return parts.join(" ");
  }

  if (ten > 0) {
    if (hundred > 0 && ten === 0) parts.push("linh");
    else if (ten === 1) parts.push("mười");
    else parts.push(DIGITS[ten] + " mươi");
    if (unit > 0) {
      if (unit === 1 && ten > 1) parts.push("mốt");
      else if (unit === 4 && ten > 1) parts.push("tư");
      else if (unit === 5 && ten > 0) parts.push("lăm");
      else parts.push(DIGITS[unit]);
    }
    return parts.join(" ");
  }

  // No ten, only a unit (and maybe a hundred).
  if (hundred > 0 && unit > 0) parts.push("linh");
  if (unit > 0) parts.push(DIGITS[unit]);
  return parts.join(" ");
}

/**
 * Convert a numeric value to Vietnamese words. Accepts number, numeric string,
 * or any value coercible to a finite number. Returns "" for zero / blank /
 * non-numeric input (so a missing amount yields an empty "bằng chữ" line).
 */
export function numberToVietnameseWords(value: unknown): string {
  if (value === null || value === undefined) return "";
  const n = Number(typeof value === "number" ? value : String(value).replace(/[^\d.\-]/g, ""));
  if (!Number.isFinite(n) || n === 0) return "";

  const negative = n < 0;
  let abs = Math.floor(Math.abs(n));
  if (abs === 0) return "";

  const groups: number[] = [];
  while (abs > 0) {
    groups.push(abs % 1000);
    abs = Math.floor(abs / 1000);
  }

  const phrases: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const block = groups[i];
    if (block === 0) continue;
    const phrase = readHundreds(block);
    const suffix = UNITS[i];
    if (phrases.length === 0) {
      phrases.push(phrase + (suffix ? ` ${suffix}` : ""));
    } else {
      // Insert a leading "linh" (zero hundred) when a middle group is < 100
      // but a higher non-zero group already filled the phrase — e.g.
      // 1_000_005 → "một triệu linh năm".
      const higherFilled = groups.slice(i + 1).some((b) => b > 0);
      if (block < 100 && higherFilled) {
        phrases.push(`linh ${phrase}${suffix ? ` ${suffix}` : ""}`);
      } else {
        phrases.push(phrase + (suffix ? ` ${suffix}` : ""));
      }
    }
  }

  const base = phrases.join(" ");
  return negative ? `âm ${base}` : base;
}

/** Convenience: append the currency word (default "đồng"). */
export function numberToVietnameseWordsCurrency(
  value: unknown,
  currency = "đồng"
): string {
  const words = numberToVietnameseWords(value);
  return words ? `${words} ${currency}` : "";
}
